import dayjs, { Dayjs } from "dayjs";

/**
 * Saved date ranges are stored as a PRESET, never as resolved dates: "year to
 * date" has to mean this year whenever the view is opened, not the year it was
 * saved in. The range is therefore resolved at read time.
 */
export const DATE_PRESETS = {
  wtd: "Week to date",
  mtd: "Month to date",
  qtd: "Quarter to date",
  ytd: "Year to date",
} as const;

export type DatePresetKey = keyof typeof DATE_PRESETS;

/**
 * Returns dayjs, not Date: the pickers run on AdapterDayjs and call .isValid()
 * on whatever the filter holds, so a native Date throws on the first render.
 */
export const presetRange = (key: DatePresetKey, now: Dayjs = dayjs()): [Dayjs, Dayjs] => {
  const start =
    key === "wtd" ? now.subtract(now.day(), "day")
    : key === "mtd" ? now.date(1)
    : key === "qtd" ? now.month(Math.floor(now.month() / 3) * 3).date(1)  // 7 -> 6 (Jul), not 2
    : now.month(0).date(1);
  return [start.startOf("day"), now];
};

/**
 * Range filter that tolerates rows with no date. MRT's betweenInclusive calls
 * .toString() on the cell value, so an undefined date — a course never started,
 * never completed — throws the moment the filter runs. Last Access and
 * Completed Date both have those rows; Assigned Date does not, which is why
 * only they crashed.
 *
 * A row with no date is outside every range, so it filters out rather than
 * erroring.
 */
export const withinDateRange = (row: any, columnId: string, filterValue: any) => {
  const value = row.getValue(columnId);
  if (!value) return false;
  const [from, to] = Array.isArray(filterValue) ? filterValue : [];
  if (!from && !to) return true;
  const at = dayjs(value).valueOf();
  if (from && at < dayjs(from).startOf("day").valueOf()) return false;
  if (to && at > dayjs(to).endOf("day").valueOf()) return false;
  return true;
};

/** Drop cleared ("") preset entries: applyDatePreset(col, "") leaves the key
 *  behind, which made the dirty-compare see a change where none renders. */
export const stripEmptyRanges = (
  ranges: Record<string, string> | undefined
): Record<string, string> =>
  Object.fromEntries(Object.entries(ranges ?? {}).filter(([, v]) => v));

/** The "final assessment" grade item, when the course has one. */
export const finalAssessmentItem = (row: any) =>
  (row?.gradeItems ?? []).find((item: any) =>
    item?.name?.toLowerCase().includes("final")
  );

/**
 * What the Final Score column shows: "" for none, a number, or "Multiple" when
 * several items are graded and none is the final assessment.
 */
export const finalScoreOf = (row: any): string => {
  const items = row?.gradeItems ?? [];
  if (items.length === 0) return "";
  if (items.length === 1) {
    return items.at(0)?.grade === -1 ? "" : items.at(0)?.grade.toFixed(0);
  }
  if (items.every((x: any) => x.grade === -1)) return "";
  const final = finalAssessmentItem(row);
  if (final) return final.grade === -1 ? "" : final.grade.toFixed(0);
  return "Multiple";
};

/** A score on screen means the course is finished, whatever completed_status says. */
export const hasFinalScore = (row: any) => finalScoreOf(row) !== "";

/** The latest grade-item finish time, or undefined when nothing has finished. */
const latestFinishOf = (row: any): string | undefined =>
  (row?.gradeItems ?? []).reduce((latest: string | undefined, item: any) => {
    const finish = item?.time_finish;
    if (!finish) return latest;
    return !latest || new Date(finish) > new Date(latest) ? finish : latest;
  }, undefined);

/**
 * What the Completed Date column shows. Moodle sometimes never flags a course
 * complete (completed_status 0, no completed_date) even though its assessment
 * finished with a grade — so a scored course falls back to the item that
 * finished it. Courses with a "Final Assessment" item use its finish time;
 * single-item CSA courses name the item after the course instead, so the last
 * resort is the latest finish among the graded items (same semantics as the
 * backend's _derive_completed_date, which only runs when the status flag is set).
 */
export const completedDateOf = (row: any): string | undefined =>
  row?.completed_date ||
  (hasFinalScore(row)
    ? finalAssessmentItem(row)?.time_finish ?? latestFinishOf(row)
    : undefined);

export interface GradeItem {
  name: string;
  state: string;
  time_start: Date;
  time_finish: Date;
  time_taken: string;
  correct: number;
  incorrect: number
  grade: number;
}

export interface CourseItem {
  course: string;
  student: string;
  email: string;
  assigned_date: Date;
  completed_date: Date;
  completed_status: boolean;
  last_access: Date;
  paid: number,
  country: string;
  state: string;
  school: string;
  gradeItems: GradeItem[];
}
